/**
 * AtlasAudioModule — mirrors `audio_zone` rows into sound, and plays the Edit
 * Mode speaker devices' clips.
 *
 * Since 2026-09-20 a client of the shared audio runtime (lib/audio/runtime.ts).
 * It used to own an engine `AudioManager` — a private AudioContext with its own
 * listener, cache and 32-voice oldest-first pool — and that arrangement carried
 * four of the audit's known gaps, all closed by the move:
 *
 *   THE LISTENER NEVER TURNED. Only `setListenerPosition` was called, so a hum
 *     stayed in the same ear when you turned around. Zones now use the one
 *     shared, listener-relative pose (camera orientation included).
 *   17 OF 20 CATALOG CLIPS 404'd. `render_asset.source_uri` is an ASSET-STORE
 *     key and was fetched beside the page; the three live zones worked only
 *     because copies sat in client/public/audio/. The store resolves it now
 *     (`c:` assets → `${ASSET_STORE_BASE}/${key}`), with retry on a transient
 *     failure.
 *   `fadeDuration`, `priority` and `kind` WERE INERT. Zones clicked on and off
 *     at the boundary whatever the editor's fade said. `kind` now picks the
 *     ROLE (ambience → a bed-region of even sound; sfx / music → a located
 *     point) and the bus; `fadeDuration` eases entry and exit; `priority`
 *     arbitrates overlapping beds (lib/audio/zones.ts, pure).
 *   A BIG ZONE WAS LOUD ONLY AT ITS HEART (a point source falling off as 1/d
 *     from the centre: a 130 m zone at gain 0.5 was ≈ 0.004 at its rim).
 *
 * Containment is judged at the PLAYER (not the camera): a chase camera drifting
 * across a boundary must not start a room you are not standing in.
 *
 * System `AtlasAudioZoneTick`, Animation / 50 — after the avatar is posed and
 * one slot after the runtime module posed the listener.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { Logger } from 'enginex/core/Logger.js';
import { audioRuntime, type LoopHandle, type VoiceHandle } from '../lib/audio/runtime.js';
import { bedEdgeWeight, bedGains, depthInside, pointCurve, roleOfKind, type BedInput } from '../lib/audio/zones.js';
import type { Bus } from '../lib/audio/buses.js';

interface ZoneRow {
  zoneId: bigint;
  worldId: bigint;
  audioAssetId: bigint;
  kind: string;
  shapeType: string;
  cx: number; cy: number; cz: number;
  radius: number;
  sx: number; sy: number; sz: number;
  gain: number;
  loop: boolean;
  fadeDuration: number;
  priority: number;
}

interface LiveZone { key: string; uri: string; loop: LoopHandle | null; shot: VoiceHandle | null; inside: boolean; lastGain: number }

/** `kind` → the category bus (so a music zone obeys the Music slider, a hum the Ambience one). */
const BUS_OF_KIND: Record<string, Bus> = { ambience: 'ambience', music: 'music', sfx: 'machinery' };
const clampFade = (s: number): number => Math.min(8, Math.max(0.05, Number.isFinite(s) ? s : 1.5));

export class AtlasAudioModule implements IModule {
  readonly name = 'AtlasAudio';

  /** All known audio zones (any world). */
  private zones = new Map<string, ZoneRow>();
  /** Zones with a live voice, keyed by zoneId string. */
  private live = new Map<string, LiveZone>();
  private speakerSeq = 0;
  /** Probe surface: the zone ids currently audible (verify:ambience-wild). */
  activeZoneIds(): string[] { return [...this.live.entries()].filter(([, z]) => z.inside).map(([k]) => k); }

  init(app: App): void {
    (globalThis as unknown as { __audioZones?: () => string[] }).__audioZones = () => this.activeZoneIds(); // probe surface
    (globalThis as unknown as { __audioZoneDiag?: () => unknown }).__audioZoneDiag = () => [...this.live.entries()].map(([k, z]) => ({ zone: k, uri: z.uri, inside: z.inside, gain: +z.lastGain.toFixed(3), asset: audioRuntime().store.state(`c:${z.uri}`) }));
    // (The AudioContext, the listener and the intro's mute are the shared runtime's.)
    bus.on('atlas:account-ready', () => { this.bindSubscriptions(); });
    app.world.addSystem(makeSystem('AtlasAudioZoneTick', Phase.Animation, 50, () => this.tick()));
  }

  // ── SpacetimeDB mirroring ─────────────────────────────────────────────────

  private bindSubscriptions(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const ping = (): void => this.onZonesChanged();
    const az = conn.db.audioZone as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
    az.onInsert(ping);
    az.onUpdate(ping);
    az.onDelete(ping);
    this.onZonesChanged(); // rows already in the subscription cache
  }

  private onZonesChanged(): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.zones.clear();
    for (const row of conn.db.audioZone.iter()) this.zones.set(String(row.zoneId), row as unknown as ZoneRow);
    // A deleted (or re-pointed) zone releases its voice; an edited one keeps playing and picks up its new numbers next frame.
    for (const [key, lz] of this.live) {
      const z = this.zones.get(key);
      if (!z || this.uriOf(z) !== lz.uri) { this.release(key, lz, 0.4); }
    }
  }

  private uriOf(zone: ZoneRow): string {
    const asset = atlas.conn?.db.renderAsset.renderAssetId.find(zone.audioAssetId);
    return asset?.sourceUri ? String(asset.sourceUri) : '';
  }

  private release(key: string, lz: LiveZone, fadeS: number): void {
    audioRuntime().dropScope(`zone:${key}`, fadeS);
    lz.shot?.stop(fadeS);
    this.live.delete(key);
  }

  // ── Per-frame zone evaluation ─────────────────────────────────────────────

  private tick(): void {
    const rt = audioRuntime();
    if (!rt.ctx) return;
    const worldId = atlas.currentWorldId;
    if (worldId === null) { for (const [k, lz] of [...this.live]) this.release(k, lz, 0.5); return; }
    const x = atlas.localX, y = atlas.localY, z = atlas.localZ;

    // Pass 1: who contains the player, and the bed arbitration across them.
    const beds: BedInput[] = [];
    const insideNow = new Map<string, number>(); // key → depth 0..1
    for (const [key, zone] of this.zones) {
      if (zone.worldId !== worldId) continue;
      const depth = depthInside(zone, x, y, z);
      if (depth <= 0) continue;
      insideNow.set(key, depth);
      if (roleOfKind(zone.kind) === 'bed-region') beds.push({ id: key, priority: zone.priority, gain: zone.gain, edge: bedEdgeWeight(zone, x, y, z) });
    }
    const bedGain = bedGains(beds);

    // Pass 2: drive every zone that is, or just was, audible.
    for (const [key, zone] of this.zones) {
      if (zone.worldId !== worldId) { const gone = this.live.get(key); if (gone) this.release(key, gone, 0.5); continue; }
      const inside = insideNow.has(key);
      let lz = this.live.get(key);
      if (!inside && !lz) continue;
      const uri = this.uriOf(zone);
      if (uri === '') continue;
      const role = roleOfKind(zone.kind);
      const fadeS = clampFade(zone.fadeDuration);
      if (!lz) { lz = { key, uri, loop: null, shot: null, inside: false, lastGain: 0 }; this.live.set(key, lz); }
      const entering = inside && !lz.inside, leaving = !inside && lz.inside;
      lz.inside = inside;

      if (!zone.loop) {
        // A one-shot zone: played once per ENTRY (a doorway chime), never re-triggered while you stand in it.
        if (entering) {
          const buf = rt.store.get(`c:${uri}`);
          if (buf) lz.shot = rt.playBuffer(buf, `zone_${zone.kind}`, { bus: BUS_OF_KIND[zone.kind] ?? 'machinery', priority: 50, gain: zone.gain, ...(role === 'point' ? { pos: { x: zone.cx, y: zone.cy, z: zone.cz }, distance: 'curve' as const, ...pointCurve(zone.radius) } : {}) });
          else lz.inside = false; // still decoding — count the entry when the clip can actually answer it
        }
        if (leaving) { this.live.delete(key); }
        continue;
      }

      if (!lz.loop) {
        lz.loop = rt.loop(`zone:${key}`, `zone_${zone.kind}`, {
          assetId: `c:${uri}`, bus: BUS_OF_KIND[zone.kind] ?? 'machinery', priority: role === 'bed-region' ? 48 : 52,
          ...(role === 'point' ? { distance: 'curve' as const, ...pointCurve(zone.radius) } : {}),
        });
        if (role === 'point') lz.loop.at({ x: zone.cx, y: zone.cy, z: zone.cz });
        Logger.debug('AtlasAudio', `zone ${zone.zoneId} (${role}) armed`);
      }
      // THE LEVEL. A bed: its arbitrated, edge-eased share. A point: on while you are inside (the panner owns distance).
      const target = !inside ? 0 : role === 'bed-region' ? (bedGain.get(key) ?? 0) : zone.gain;
      // `fadeDuration` eases the ENTRY and the EXIT; in between the bed follows the edge band smoothly frame to frame.
      const ramp = entering || leaving ? fadeS : 0.25;
      if (entering || leaving || Math.abs(target - lz.lastGain) > 0.002 || target > 0) lz.loop.set(target, 1, ramp);
      lz.lastGain = target;
      if (!inside && leaving) {
        // Let the fade finish, then forget the handle (the runtime virtualizes the silent loop on its own meanwhile).
        const held = lz;
        window.setTimeout(() => { if (this.live.get(key) === held && !held.inside) this.release(key, held, 0.05); }, fadeS * 1000 + 300);
      }
    }
  }

  /**
   * Play a catalog clip (render_asset kind=audio) at a world position — the
   * Edit Mode speaker device. Resolves + decodes lazily through the shared
   * store; returns a stop function (a press while it plays stops it).
   */
  playClipAt(assetId: bigint, pos: { x: number; y: number; z: number }, opts: { gain?: number; loop?: boolean; bus?: 'ambient' | 'sfx' | 'music' } = {}): () => void {
    const conn = atlas.conn;
    const rt = audioRuntime();
    if (!conn || !rt.ctx) return () => { /* audio not ready */ };
    const asset = conn.db.renderAsset.renderAssetId.find(assetId);
    if (!asset || !asset.sourceUri) return () => { /* unknown clip */ };
    const id = `c:${String(asset.sourceUri)}`;
    const busName: Bus = opts.bus === 'music' ? 'music' : opts.bus === 'ambient' ? 'ambience' : 'machinery';
    const gain = opts.gain ?? 0.8;
    // A speaker is a small located source: full level within 3 m, 1/d beyond.
    const spatial = { pos, distance: 'curve' as const, refDistance: 3, rolloff: 1 };
    if (opts.loop) {
      const scope = `speaker:${++this.speakerSeq}`;
      const loop = rt.loop(scope, 'zone_speaker', { assetId: id, bus: busName, priority: 54, distance: 'curve', refDistance: 3, rolloff: 1 });
      loop.at(pos);
      // The clip may still be decoding: keep asking until it starts (the loop API starts on the first set() after the decode).
      let tries = 0;
      const timer = window.setInterval(() => { loop.set(gain, 1, 0.2); if (++tries > 40 || rt.store.state(id) === 'ready' || rt.store.state(id) === 'failed') window.clearInterval(timer); }, 250);
      loop.set(gain, 1, 0.2);
      return () => { window.clearInterval(timer); rt.dropScope(scope, 0.15); };
    }
    let voice: VoiceHandle | null = null;
    let stopped = false;
    void rt.store.load(id).then((buf) => { if (buf && !stopped) voice = rt.playBuffer(buf, 'zone_speaker', { bus: busName, priority: 54, gain, ...spatial }); });
    return () => { stopped = true; voice?.stop(0.1); };
  }

  dispose(): void {
    for (const [k, lz] of [...this.live]) this.release(k, lz, 0.05);
    // (The context is the shared runtime's — a module never closes it.)
  }
}
